import React, { FC, ReactNode, useEffect, useState } from 'react';
import { CircularProgress, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';

import { AppIcon } from 'src/shared/components';
import { ComponentWithFade } from 'src/shared/hocs/ComponentWithFade';
import { usePrevious } from 'src/shared/hooks';
import './ComponentWithGlobalInit.styles.scss';

/**
 * This component is used to load initial data and show a global loading screen on init
 */

interface Props {
  data?: Record<string, unknown>;
  shouldResetProcess?: boolean;
  shouldCompleteProcess?: boolean;
  shouldWait?: boolean;
  className?: string;
  children?: ReactNode;
}

export const ComponentWithGlobalInit: FC<Props> = ({
  data,
  children,
  shouldCompleteProcess,
  shouldWait = true,
  className = '',
}) => {
  const theme = useTheme();
  const [hasBuffer, setHasBuffer] = useState<boolean>(!shouldWait);
  const [progress, setProgress] = useState<number>(0);
  const previousData = usePrevious(data);
  const shouldDisplay = shouldCompleteProcess || (hasBuffer && progress >= 100);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (!previousData || !data) {
      return;
    }

    if (!shouldDisplay && progress !== 100 && Object.values(data).every((item) => !isEmpty(item))) {
      setProgress(100);
      return;
    }

    if (progress >= 100) {
      if (shouldWait) {
        timeout = setTimeout(() => {
          setHasBuffer(true);
        }, 1000);
      }
    }

    for (const entry of Object.entries(previousData)) {
      const [key, value] = entry;

      // compare memory reference to see if it's same object/value
      if (data[key] === value) {
        continue;
      }

      const shouldReset = isEmpty(data[key]) && !isEmpty(value);

      if (progress >= 100 && shouldReset) {
        setProgress(0);
        break;
      }

      setProgress(progress + 100 / Object.keys(data).length);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [progress, data, previousData]);

  return (
    <div className={`${className} position-relative`}>
      {!shouldDisplay && (
        <div className="min-vh-100">
          {data && <LinearProgress className="progress" variant="determinate" value={progress} />}
          <div className="loading-wrapper">
            <AppIcon theme={theme} />
            <CircularProgress size={18} />
          </div>
        </div>
      )}
      <ComponentWithFade show={shouldDisplay} sx={{ display: 'flex', width: '100%' }}>
        {shouldDisplay ? children : null}
      </ComponentWithFade>
    </div>
  );
};
